import { useCallback, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import Spinner from 'react-bootstrap/Spinner'
import Tab from 'react-bootstrap/Tab'
import Tabs from 'react-bootstrap/Tabs'
import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { deleteGroup } from '../../../../types'
import { actionApiCall } from '../../api/actionApiCall'
import { useAuth } from '../../auth/AuthContext'
import { useGroupSnapshot } from '../../hooks/useGroupSnapshot'
import { useInvalidateGroups } from '../../hooks/useGroups'
import { formatDateTime } from '../../utils/format'
import { BalancesTab } from './BalancesTab'
import { GroupFormModal } from './GroupFormModal'
import { HistoryTab } from './HistoryTab'
import { MembersTab } from './MembersTab'
import { ShareLinksModal } from './ShareLinksModal'

export function GroupDetailPage() {
    const { groupId } = useParams<{ groupId: string }>()
    const { user } = useAuth()
    const navigate = useNavigate()
    const invalidateGroups = useInvalidateGroups()
    const [showForm, setShowForm] = useState(false)
    const [showSharing, setShowSharing] = useState(false)
    const [zalozka, setZalozka] = useState('historie')

    const { snapshot, fromCache, syncedAt, loading, refreshing, failed, online, refresh } = useGroupSnapshot(groupId ?? '')

    // Po každém zápisu se vynutí čerstvý stav ze serveru - ten je zdrojem pravdy.
    const handleChanged = useCallback(() => {
        void refresh(true)
        void invalidateGroups()
    }, [refresh, invalidateGroups])

    if (!groupId) {
        return <Navigate to="/" replace />
    }

    if (loading) {
        return <Spinner animation="border" />
    }

    if (!snapshot) {
        return (
            <Alert variant={failed && !online ? 'secondary' : 'warning'}>
                {failed && !online
                    ? 'Jste offline a tato skupina zatím není uložená v zařízení. Připojte se k internetu a zkuste to znovu.'
                    : 'Skupina nebyla nalezena nebo do ní nemáte přístup.'}
            </Alert>
        )
    }

    const group = snapshot.group
    const canWrite = online

    const handleDelete = async () => {
        if (!window.confirm(`Opravdu smazat skupinu ${group.name} včetně celé historie? Tuto akci nelze vrátit.`)) {
            return
        }
        const result = await actionApiCall(() => deleteGroup({ path: { groupId } }), { successMessage: 'Skupina byla smazána.' })
        if (result !== undefined) {
            await invalidateGroups()
            navigate('/', { replace: true })
        }
    }

    return (
        <>
            <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
                <div className="min-w-0">
                    <h1 className="h4 mb-1 text-truncate">{group.name}</h1>
                    <div className="small text-secondary">
                        Hlavní měna {group.baseCurrencyCode}
                        {group.currencyCodes.length > 1 &&
                            ` · další měny ${group.currencyCodes.filter((code) => code !== group.baseCurrencyCode).join(', ')}`}
                    </div>
                </div>

                <div className="d-flex align-items-center gap-1 flex-shrink-0">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        className="su-tappable"
                        aria-label="Načíst znovu ze serveru"
                        disabled={refreshing}
                        onClick={() => void refresh(true)}
                    >
                        {refreshing ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <i className="bi bi-arrow-clockwise" aria-hidden="true" />
                        )}
                    </Button>

                    {user?.isAdmin && (
                        <Dropdown align="end">
                            <Dropdown.Toggle variant="outline-secondary" size="sm" className="su-tappable" id="menu-skupiny" aria-label="Akce skupiny">
                                <i className="bi bi-three-dots-vertical" aria-hidden="true" />
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                                <Dropdown.Item disabled={!canWrite} onClick={() => setShowForm(true)}>
                                    Upravit skupinu
                                </Dropdown.Item>
                                <Dropdown.Item disabled={!canWrite} onClick={() => setShowSharing(true)}>
                                    Veřejné sdílení
                                </Dropdown.Item>
                                <Dropdown.Divider />
                                <Dropdown.Item className="text-danger" disabled={!canWrite} onClick={() => void handleDelete()}>
                                    Smazat skupinu
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    )}
                </div>
            </div>

            {!online && (
                <Alert variant="secondary" className="py-2 small" data-testid="offline-pruh">
                    <i className="bi bi-wifi-off me-1" aria-hidden="true" />
                    Offline — zobrazena uložená data{syncedAt ? ` z ${formatDateTime(syncedAt)}` : ''}. Zápis je dočasně nedostupný.
                </Alert>
            )}

            {online && fromCache && (
                <Alert variant="light" className="py-2 small border">
                    Zobrazena uložená data, synchronizuji se serverem…
                </Alert>
            )}

            {group.archived && (
                <Alert variant="secondary" className="py-2">
                    Skupina je archivovaná.
                </Alert>
            )}

            {/* mountOnEnter: skryté záložky se nevykreslují, dokud na ně uživatel nepřepne */}
            <Tabs activeKey={zalozka} onSelect={(klic) => setZalozka(klic ?? 'historie')} className="mb-3" fill mountOnEnter>
                <Tab eventKey="historie" title="Historie">
                    <HistoryTab
                        group={group}
                        transactions={snapshot.transactions}
                        truncated={snapshot.truncated}
                        canWrite={canWrite}
                        onChanged={handleChanged}
                    />
                </Tab>
                <Tab eventKey="dluhy" title="Dluhy">
                    <BalancesTab
                        group={group}
                        balances={snapshot.balances}
                        transactions={snapshot.transactions}
                        canWrite={canWrite}
                        onChanged={handleChanged}
                        onShowHistory={() => setZalozka('historie')}
                    />
                </Tab>
                <Tab eventKey="clenove" title="Členové">
                    <MembersTab group={group} canWrite={canWrite} onChanged={handleChanged} />
                </Tab>
            </Tabs>

            <div className="d-flex align-items-center justify-content-between mt-3">
                <Button variant="link" className="px-0" onClick={() => navigate('/')}>
                    <i className="bi bi-arrow-left me-1" aria-hidden="true" />
                    Zpět na skupiny
                </Button>
                {syncedAt && <span className="small text-secondary">Aktualizováno {formatDateTime(syncedAt)}</span>}
            </div>

            {showForm && (
                <GroupFormModal show={showForm} group={group} onClose={() => setShowForm(false)} onSaved={handleChanged} />
            )}

            {showSharing && <ShareLinksModal show={showSharing} groupId={groupId} onClose={() => setShowSharing(false)} />}
        </>
    )
}
